import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

const Brokers = () => {
  const [brokers, setBrokers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'grid' or 'table'
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBrokers = async () => {
      setIsLoading(true);
      try {
        const res = await fetch('https://api.happychd.com/api/broker');
        if (!res.ok) {
          throw new Error('Failed to fetch brokers');
        }
        const data = await res.json();
        setBrokers(data);
        setError(null);
      } catch (error) {
        console.error('Error fetching Brokers:', error);
        setError('Failed to load Brokers. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrokers();
  }, []);

  const handleAction = (type, id, userId) => {
    if (type === 'deposit') navigate(`/broker/deposit/${id}/${userId}`);
    if (type === 'withdraw') navigate(`/broker/withdraw/${id}/${userId}`);
    if (type === 'transactions') navigate(`/broker/transactions/${id}/${userId}`);
    if (type === 'payment-status') navigate(`/broker/payment-status/${id}/${userId}`);
    if (type === 'balance-withdrawal') navigate(`/broker/balance-withdrawal/${id}/${userId}`);
  };

  // Filter brokers based on search term
  const filteredBrokers = brokers.filter(broker =>
    broker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    broker.userId.toString().includes(searchTerm) ||
    broker.referredBy?.userId.toLowerCase().includes(searchTerm)
);

  const groupBrokersByReferrer = (brokers) => {
    const grouped = {};

    brokers.forEach(broker => {
      const referrerName = broker.referredBy?.userId || 'Unassigned';
      if (!grouped[referrerName]) {
        grouped[referrerName] = [];
      }
      grouped[referrerName].push(broker);
    });

    return grouped;
  };


  const groupedBrokers = groupBrokersByReferrer(filteredBrokers);

  // Action button component to reduce repetition
  const ActionButton = ({ type, id, userId, color, hoverColor, icon, label, compact = false }) => {
    // Extract amount from label if present (for buttons with amounts)
    const hasAmount = label.includes('₹');
    let buttonText = label;
    let amountText = '';

    if (hasAmount) {
      const parts = label.split('₹');
      buttonText = parts[0].trim();
      amountText = `₹${parts[1]}`;
    }

    if (compact) {
      return (
        <button
          onClick={() => handleAction(type, id, userId)}
          className={`${color} ${hoverColor} text-white p-2 rounded-full text-sm font-medium transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center`}
          title={label}
        >
          <span className="text-xs">{icon}</span>
        </button>
      );
    }

    return (
      <button
        onClick={() => handleAction(type, id, userId)}
        className={`${color} ${hoverColor} text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 w-full flex flex-col items-center justify-center shadow-sm hover:shadow`}
      >
        {hasAmount && (
          <span className="text-sm font-bold mb-1">{amountText}</span>
        )}
        <span className="flex items-center gap-1">
          {icon && <span className="text-xs">{icon}</span>}
          <span>{buttonText}</span>
        </span>
      </button>
    );
  };

  // Card skeleton for loading state
  const CardSkeleton = () => (
    <div className="animate-pulse bg-white rounded-xl shadow-md p-6 mb-4">
      <div className="flex items-center mb-4">
        <div className="h-12 w-12 bg-gray-200 rounded-full mr-4"></div>
        <div>
          <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-20"></div>
        </div>
      </div>
      <div className="h-5 bg-gray-200 rounded w-24 mb-4"></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="h-10 bg-gray-200 rounded-lg"></div>
        <div className="h-10 bg-gray-200 rounded-lg"></div>
        <div className="h-10 bg-gray-200 rounded-lg"></div>
        <div className="h-10 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            {/* Left: Title */}
            <div>
              <h2 className="text-3xl font-bold text-gray-800">
                <span className="text-orange-500">Broker</span> Management
              </h2>
              {/* <p className="text-gray-500 mt-1">Manage your broker relationships and transactions</p> */}
            </div>

            {/* Right: Search and Button */}
            <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
              {/* Search bar */}
              <div className="relative w-full md:w-64">
                <input
                  type="text"
                  placeholder="Search by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </div>

              {/* View toggle */}
              <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-1 rounded-md ${viewMode === 'grid'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 rounded-md ${viewMode === 'table'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
              </div>

              {/* Navigate Button */}
              <Link
                to="/all-brokers"
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                View All Brokers
              </Link>
            </div>
          </div>

          {/* Stats overview */}
          {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-orange-500">
              <p className="text-gray-500 text-sm">Total Brokers</p>
              <p className="text-2xl font-bold">{brokers.length}</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-green-500">
              <p className="text-gray-500 text-sm">Total Deposits</p>
              <p className="text-2xl font-bold text-green-600">₹{brokers.reduce((total, broker) => {
                const deposits = broker.transactions?.filter(t => t.type === 'deposit').reduce((sum, t) => sum + t.amount, 0) || 0;
                return total + deposits;
              }, 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-red-500">
              <p className="text-gray-500 text-sm">Total Withdrawals</p>
              <p className="text-2xl font-bold text-red-600">₹{brokers.reduce((total, broker) => {
                const withdrawals = broker.transactions?.filter(t => t.type === 'withdraw').reduce((sum, t) => sum + t.amount, 0) || 0;
                return total + withdrawals;
              }, 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl shadow-md p-4 border-l-4 border-blue-500">
              <p className="text-gray-500 text-sm">Active Brokers</p>
              <p className="text-2xl font-bold text-blue-600">{brokers.filter(broker => broker.netBalance > 0).length}</p>
            </div>
          </div> */}
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, index) => (
              <CardSkeleton key={index} />
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-4 shadow-sm border border-red-200">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              {error}
            </div>
          </div>
        ) : (
          <>
            {filteredBrokers.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-8 text-center">
                <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Brokers Found</h3>
                <p className="text-gray-500">No brokers match your current search criteria.</p>
              </div>
            ) : (
              <>
                {/* Table view */}
                {viewMode === 'table' && (
                  <div className="bg-white shadow-md rounded-xl overflow-hidden border border-gray-100">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gradient-to-r from-orange-50 to-orange-100">
                          <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Guarantee</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Broker</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">User ID</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">ID %</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Commission</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Net Balance</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Raw ID Settled</th>
                            <th className="px-6 py-4 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Object.entries(groupedBrokers).map(([referrerName, brokers]) => (
                            <React.Fragment key={referrerName}>
                              {/* Row for referrer name */}
                              <tr className="bg-gray-100">
                                <td colSpan={8} className="px-6 py-2 font-bold text-orange-600 uppercase">
                                  Guarantee : {referrerName}
                                </td>
                              </tr>
                              {(() => {
                                const totals = brokers.reduce(
                                  (acc, broker) => {
                                    const deposit = broker.transactions?.filter(t => t.type === 'deposit').reduce((sum, t) => sum + t.amount, 0) || 0;
                                    const withdraw = broker.transactions?.filter(t => t.type === 'withdraw').reduce((sum, t) => sum + t.amount, 0) || 0;
                                    const paymentStatus = broker.paymentStatus?.reduce((sum, p) => sum + p.amount, 0) || 0;
                                    const balanceHistory = broker.balanceHistory?.reduce((sum, b) => sum + b.amount, 0) || 0;
                                    const rawIdSettled = ((withdraw + balanceHistory - deposit) * (broker.idPercentage / 100)) || 0;

                                    acc.deposit += deposit;
                                    acc.withdraw += withdraw;
                                    acc.paymentStatus += paymentStatus;
                                    acc.balanceHistory += balanceHistory;
                                    acc.rawIdSettled += rawIdSettled;
                                    return acc;
                                  },
                                  { deposit: 0, withdraw: 0, paymentStatus: 0, balanceHistory: 0, rawIdSettled: 0 }
                                );

                                return (
                                  <tr className="bg-yellow-50 text-sm text-gray-700 font-semibold">
                                    <td className="px-6 py-3">Total</td>
                                    <td colSpan="2" className="px-6 py-3">Deposit: ₹{totals.deposit.toFixed(2)}</td>
                                    <td className="px-6 py-3">Withdraw: ₹{totals.withdraw.toFixed(2)}</td>
                                    <td className="px-6 py-3">Payment: ₹{Math.abs(totals.paymentStatus).toFixed(2)}</td>
                                    <td className="px-6 py-3">Balance: ₹{Math.abs(totals.balanceHistory).toFixed(2)}</td>
                                    <td className="px-6 py-3 text-orange-600">Raw ID Settled: ₹{Math.abs(totals.rawIdSettled).toFixed(2)}</td>
                                    <td></td>
                                  </tr>
                                );
                              })()}


                              {/* Rows for brokers under this referrer */}
                              {brokers.map(broker => {
                                const totalDeposit = broker.transactions?.filter(t => t.type === 'deposit').reduce((sum, t) => sum + t.amount, 0) || 0;
                                const totalWithdraw = broker.transactions?.filter(t => t.type === 'withdraw').reduce((sum, t) => sum + t.amount, 0) || 0;
                                const totalPaymentStatus = broker.paymentStatus?.reduce((sum, p) => sum + p.amount, 0) || 0;
                                const totalBalanceHistory = broker.balanceHistory?.reduce((sum, b) => sum + b.amount, 0) || 0;
                                const rawIdSettled = ((totalWithdraw + totalBalanceHistory - totalDeposit) * (broker.idPercentage / 100)).toFixed(2);

                                return (
                                  <tr key={broker._id} className="hover:bg-gray-50 transition-colors duration-150">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{referrerName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-lg flex items-center justify-center text-lg font-bold">
                                          {broker.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="ml-4">
                                          <div className="text-sm font-medium text-gray-900">{broker.name}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{broker.userId}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{broker.idPercentage}%</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{broker.commission}%</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${broker.netBalance >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        ₹{broker.netBalance}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-600">₹{rawIdSettled}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex justify-center gap-2">
                                        <ActionButton type="deposit" id={broker._id} userId={broker.userId} color="bg-green-500" hoverColor="hover:bg-green-600" icon="↑" label="Deposit" compact={true} />
                                        <ActionButton type="withdraw" id={broker._id} userId={broker.userId} color="bg-red-500" hoverColor="hover:bg-red-600" icon="↓" label="Withdraw" compact={true} />
                                        <ActionButton type="transactions" id={broker._id} userId={broker.userId} color="bg-blue-500" hoverColor="hover:bg-blue-600" icon="↔" label="Transactions" compact={true} />
                                        <ActionButton type="payment-status" id={broker._id} userId={broker.userId} color="bg-yellow-500" hoverColor="hover:bg-yellow-600" icon="₹" label="Payment Status" compact={true} />
                                        <ActionButton type="balance-withdrawal" id={broker._id} userId={broker.userId} color="bg-cyan-500" hoverColor="hover:bg-cyan-600" icon="⟲" label="Balance Withdrawal" compact={true} />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </tbody>

                      </table>
                    </div>
                  </div>
                )}

                {/* Grid view */}
                {viewMode === 'grid' && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredBrokers.map((broker) => {
                      const totalDeposit = broker.transactions?.filter(t => t.type === 'deposit').reduce((sum, t) => sum + t.amount, 0) || 0;
                      const totalWithdraw = broker.transactions?.filter(t => t.type === 'withdraw').reduce((sum, t) => sum + t.amount, 0) || 0;
                      const totalPaymentStatus = broker.paymentStatus?.reduce((sum, p) => sum + p.amount, 0) || 0;
                      const totalBalanceHistory = broker.balanceHistory?.reduce((sum, b) => sum + b.amount, 0) || 0;
                      const rawIdSettled = ((totalWithdraw + totalBalanceHistory - totalDeposit) * (broker.idPercentage / 100)).toFixed(2);

                      return (
                        <div
                          key={broker._id}
                          className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-all duration-200 hover:shadow-lg"
                        >
                          <div className="p-6 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                              {/* Left side: Sign and Name */}
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-14 w-14 bg-gradient-to-br from-orange-400 to-orange-600 text-white rounded-xl flex items-center justify-center text-xl font-bold">
                                  {broker.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="ml-4">
                                  <h3 className="font-bold text-xl text-gray-800">{broker.name}</h3>
                                  <p className="text-gray-500">ID: {broker.userId}</p>
                                </div>
                              </div>

                              {/* Badge for net balance */}
                              <div>
                                <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${broker.netBalance >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  ₹{broker.netBalance}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mt-4">
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-gray-500 text-xs">ID Percentage</p>
                                <p className="text-lg font-bold">{broker.idPercentage}%</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-gray-500 text-xs">Commission</p>
                                <p className="text-lg font-bold">{broker.commission}%</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-gray-500 text-xs">Raw ID Settled</p>
                                <p className="text-lg font-bold text-orange-600">₹{rawIdSettled}</p>
                              </div>
                              <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="text-gray-500 text-xs">Balance History</p>
                                <p className="text-lg font-bold">₹{Math.abs(totalBalanceHistory)}</p>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 grid grid-cols-5 gap-2 bg-gray-50">
                            <ActionButton
                              type="deposit"
                              id={broker._id}
                              userId={broker.userId}
                              color="bg-green-500"
                              hoverColor="hover:bg-green-600"
                              icon="↑"
                              label={`Deposit ₹${totalDeposit}`}
                              compact={true}
                            />
                            <ActionButton
                              type="withdraw"
                              id={broker._id}
                              userId={broker.userId}
                              color="bg-red-500"
                              hoverColor="hover:bg-red-600"
                              icon="↓"
                              label={`Withdraw ₹${totalWithdraw}`}
                              compact={true}
                            />
                            <ActionButton
                              type="transactions"
                              id={broker._id}
                              userId={broker.userId}
                              color="bg-blue-500"
                              hoverColor="hover:bg-blue-600"
                              icon="↔"
                              label="Transactions"
                              compact={true}
                            />
                            <ActionButton
                              type="payment-status"
                              id={broker._id}
                              userId={broker.userId}
                              color="bg-yellow-500"
                              hoverColor="hover:bg-yellow-600"
                              icon="₹"
                              label={`Payment Status ₹${Math.abs(totalPaymentStatus)}`}
                              compact={true}
                            />
                            <ActionButton
                              type="balance-withdrawal"
                              id={broker._id}
                              userId={broker.userId}
                              color="bg-cyan-500"
                              hoverColor="hover:bg-cyan-600"
                              icon="⟲"
                              label={`Balance Withdrawal ₹${Math.abs(totalBalanceHistory)}`}
                              compact={true}
                            />
                          </div>

                          {/* Detail view and actions */}
                          <div className="p-4 border-t border-gray-100">
                            <div className="flex flex-wrap gap-2">
                              <ActionButton
                                type="deposit"
                                id={broker._id}
                                userId={broker.userId}
                                color="bg-green-500"
                                hoverColor="hover:bg-green-600"
                                icon="↑"
                                label={`Deposit ₹${totalDeposit}`}
                              />
                              <ActionButton
                                type="withdraw"
                                id={broker._id}
                                userId={broker.userId}
                                color="bg-red-500"
                                hoverColor="hover:bg-red-600"
                                icon="↓"
                                label={`Withdraw ₹${totalWithdraw}`}
                              />
                              <ActionButton
                                type="transactions"
                                id={broker._id}
                                userId={broker.userId}
                                color="bg-blue-500"
                                hoverColor="hover:bg-blue-600"
                                icon="↔"
                                label="Transactions"
                              />
                              <ActionButton
                                type="payment-status"
                                id={broker._id}
                                userId={broker.userId}
                                color="bg-yellow-500"
                                hoverColor="hover:bg-yellow-600"
                                icon="₹"
                                label={`Payment Status ₹${Math.abs(totalPaymentStatus)}`}
                              />
                              <ActionButton
                                type="balance-withdrawal"
                                id={broker._id}
                                userId={broker.userId}
                                color="bg-cyan-500"
                                hoverColor="hover:bg-cyan-600"
                                icon="⟲"
                                label={`Balance Withdrawal ₹${Math.abs(totalBalanceHistory)}`}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Brokers;